import { useLocalSearchParams, useRouter } from 'expo-router';
import { usePostHog } from 'posthog-js/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Dimensions,
  Easing,
  Linking,
  Modal,
  Platform,
  Animated as RNAnimated,
  Share,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import Logo from '../assets/images/logo.svg';
import { REDESIGN_COLORS } from '../styles/colors';
import DesktopHeader from '../components/common/DesktopHeader';
import MobileHeader from '../components/common/MobileHeader';
import AddCarModal from '../components/recommendations/AddCarModal';

const BREAKPOINT_WIDTH = 768;

/**
 * Validates if a string is a valid UUID v4 format
 * @param {string} str - String to validate
 * @returns {boolean} True if valid UUID
 */
const isValidUUID = (str) => {
  if (!str || typeof str !== 'string') {
    return false;
  }
  // UUID v4 regex pattern
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

import CustomShareModal from '../components/common/CustomShareModal';
import InlineNotification from '../components/common/InlineNotification';
import LoadingScreen from '../components/common/LoadingScreen';
import Footer from '../components/landing/sections/Footer';
import ListingsFeed from '../components/listings/ListingsFeed';
import ProductData from '../components/product_page/ProductData';
import ProductFit from '../components/product_page/ProductFit';
import ProductInfo from '../components/product_page/ProductInfo';
import ProductSide from '../components/product_page/ProductSide';
import SkeletonPlaceholder from '../components/ui/SkeletonPlaceholder';
import {
  PRODUCT_PAGE_GENERATING_CONFIG,
  PRODUCT_PAGE_LOADING_CONFIG,
} from '../config/loadingScreenConfigs';
import { landingPageContent } from '../constants/Content';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { ListingsFeedProvider } from '../context/ListingsFeedContext';
import { ProductDetailProvider, useProductDetail } from '../context/ProductDetailContext';
import { useNavLinks } from '../hooks/useNavLinks';
import { useRealtimeRecommendations } from '../hooks/useRealtimeRecommendations';
import { useReportRealtime } from '../hooks/useReportRealtime';
import { apiService } from '../services/apiService';
import { trackConversion, trackEngagement } from '../utils/gtmHelpers';
import logger from '../utils/logger';
import {
  transformFitScoreForProductFit,
  transformPersonalizedContentForProductInfo,
  transformVehicleDataForProductData,
  transformVehicleDataForProductSide,
} from '../utils/productDataTransformers';
import {
  getPageContext,
  getSessionContext,
  startPageTracking,
  trackProductView,
  trackSessionEvent,
} from '../utils/sessionTrackingHelper';
import supabase from '../utils/supabaseClient';
import {
  getRecommendationContext,
  getVehicleProperties,
  normalizeVehicleData,
} from '../utils/vehiclePropertyHelper';

/**
 * Memoized wrapper for the ListingsFeed section.
 * Prevents the feed from re-rendering when unrelated parent state changes
 * (e.g. ProductFit tab switches, scroll position, share modal).
 */
const MemoizedListingsFeed = memo(({ modelId, vehicleModel, fitScore, vehicleModelImageUrl }) => (
  <ListingsFeedProvider modelId={modelId}>
    <ListingsFeed
      modelId={modelId}
      vehicleModel={vehicleModel}
      fitScore={fitScore}
      vehicleModelImageUrl={vehicleModelImageUrl}
    />
  </ListingsFeedProvider>
));
MemoizedListingsFeed.displayName = 'MemoizedListingsFeed';

/**
 * ProductDetailPage - Wrapper component with ProductDetailProvider
 * Provides context to all child components for data orchestration
 *
 * @param {string} mode - 'live' (default) for real-time data, or 'shared' for snapshot data
 * @param {object} snapshotData - Snapshot data when mode='shared' (from get-shared-snapshot edge function)
 */
const ProductDetailPage = ({ mode = 'live', snapshotData = null, bottomInset = 0 }) => {
  const params = useLocalSearchParams();
  const router = useRouter();

  // In shared mode, extract model_id from snapshot data
  const rawModelId =
    mode === 'shared' && snapshotData?.vehicle_model_snapshot?.model_id
      ? snapshotData.vehicle_model_snapshot.model_id
      : params.id;

  // Validate model_id format
  const model_id = useMemo(() => {
    if (!isValidUUID(rawModelId)) {
      logger.error('[ProductDetailPage] Invalid model_id format:', rawModelId);
      return null;
    }
    return rawModelId;
  }, [rawModelId]);

  // Early return if model_id is invalid
  if (!model_id) {
    return (
      <View style={styles.contentWrapper}>
        <ErrorView
          error="Invalid vehicle ID. Please check the URL and try again."
          onRetry={() => router.back()}
          onBack={() => router.push('/home')}
        />
      </View>
    );
  }

  // In shared mode, don't use ProductDetailProvider - pass snapshot data directly
  if (mode === 'shared') {
    return <ProductDetailPageContent mode={mode} snapshotData={snapshotData} model_id={model_id} bottomInset={bottomInset} />;
  }

  // In live mode, use ProductDetailProvider for real-time data
  return (
    <ProductDetailProvider model_id={model_id}>
      <ProductDetailPageContent mode={mode} model_id={model_id} bottomInset={bottomInset} />
    </ProductDetailProvider>
  );
};

/**
 * ErrorView - Displays error UI with retry and navigation options
 * Provides user-friendly error messages and actionable recovery options
 */
const ErrorView = ({ error, onRetry, onBack }) => (
  <View style={styles.errorContainer}>
    <View style={styles.errorContent}>
      <Icon name="alert-circle-outline" size={64} color="#FF6B6B" style={styles.errorIcon} />
      <Text style={styles.errorTitle}>Oops! Something went wrong</Text>
      <Text style={styles.errorMessage}>{error || 'Unable to load vehicle details'}</Text>
      <TouchableOpacity style={styles.retryButton} onPress={onRetry}>
        <Icon name="refresh" size={20} color="#FFFFFF" style={styles.retryButtonIcon} />
        <Text style={styles.retryButtonText}>Try Again</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.errorBackButton} onPress={onBack}>
        <Text style={styles.errorBackButtonText}>Back to Dashboard</Text>
      </TouchableOpacity>
    </View>
  </View>
);

/**
 * ProductDetailPageContent - Inner component that consumes ProductDetailContext or snapshot data
 * Handles all UI rendering, data transformation, and user interactions
 *
 * @param {string} mode - 'live' or 'shared'
 * @param {object} snapshotData - Snapshot data when mode='shared'
 * @param {string} model_id - Model ID for tracking
 */
const ProductDetailPageContent = ({ mode = 'live', snapshotData = null, model_id, bottomInset = 0 }) => {
  const router = useRouter();
  const params = useLocalSearchParams();
  const posthog = usePostHog();

  // Screen width tracking for responsive layout
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);

  // Track screen width changes
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });
    return () => subscription?.remove();
  }, []);

  // Determine if mobile view
  const isMobile = screenWidth < BREAKPOINT_WIDTH;

  // Loading modal and notification state management
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [showTimeoutNotification, setShowTimeoutNotification] = useState(false);
  const hasTimedOut = useRef(false);

  // Track which model_id has had product_viewed event fired (prevent duplicates)
  const productViewTrackedRef = useRef(null);

  // Track which model_id has had PostHog product_detail_viewed event fired (prevent duplicates)
  const posthogViewTrackedRef = useRef(null);

  // Reset timeout flag when navigating to a new vehicle
  useEffect(() => {
    hasTimedOut.current = false;
    setShowLoadingModal(false);
    setShowTimeoutNotification(false);
  }, [params.id]);

  // IMPORTANT: Always call useProductDetail unconditionally at the top level
  // In shared mode, we'll just ignore the returned values
  let contextData = null;
  try {
    contextData = useProductDetail();
  } catch (error) {
    // Context not available (expected in shared mode or when provider is missing)
    logger.warn('[ProductDetailPage] ProductDetailContext not available', error);
    contextData = {
      isLoadingVehicle: false,
      isLoadingFitScore: false,
      isLoadingPersonalizedContent: false,
      error: null,
      vehicleData: null,
    };
  }

  // Determine data source based on mode
  let vehicleData,
    fitScoreData,
    personalizedContent,
    onboardingData,
    isLoadingVehicle,
    _isLoadingFitScore,
    isLoadingPersonalizedContent,
    error,
    refetchData,
    responseStatus;

  if (mode === 'shared') {
    // Use snapshot data
    vehicleData = snapshotData?.vehicle_model_snapshot || null;

    // Extract sharer's fit score with personalized price range
    const sharerFitScore = snapshotData?.page_content?.sharer_overall_fit_score;
    fitScoreData = sharerFitScore
      ? {
          score_value: sharerFitScore.score_value,
          estimated_price_range: sharerFitScore.estimated_price_range, // Use personalized price from fit_scores
          data_fidelity: 'confirmed', // Mark snapshot data as complete
        }
      : null;

    personalizedContent = snapshotData?.page_content
      ? { personalized_content: snapshotData.page_content }
      : null;

    // Extract sharer's onboarding data for calculator (driving plan and annual distance)
    onboardingData = snapshotData?.page_content?.sharer_onboarding_data || null;

    isLoadingVehicle = false;
    _isLoadingFitScore = false;
    isLoadingPersonalizedContent = false;
    error = null;
    refetchData = () => {}; // No-op in shared mode
    responseStatus = null; // Not applicable in shared mode
  } else {
    // Use live context data
    ({
      vehicleData,
      fitScoreData,
      personalizedContent: personalizedContent,
      onboardingData,
      isLoadingVehicle,
      _isLoadingFitScore,
      isLoadingPersonalizedContent,
      error,
      refetchData,
      responseStatus,
    } = contextData);
  }

  // Derived state for data loading (pure - no circular dependencies)
  // Show loading if any data is being fetched, fitScoreData is not available,
  // OR data_fidelity is not yet ready to display (placeholder states should keep loading)
  // ProductDetailPage requires FULL data ('confirmed'), not just essentials
  const isDataReady = fitScoreData?.data_fidelity === 'confirmed';
  const shouldShowLoading =
    isLoadingVehicle || isLoadingPersonalizedContent || !fitScoreData || !isDataReady;

  // Determine loading type based on response status
  // 'COMPLETE' = content exists, fast load; 'PERSONALIZED_CONTENT_PENDING' = generating, slow load
  const loadingType = responseStatus === 'COMPLETE' ? 'loading' : 'generating';

  // Derived state for loading modal visibility
  // Show modal ONLY when generating new content (not for loading existing completed recommendations)
  // For existing data (loadingType === 'loading'), use skeletons instead
  // Don't show modal after timeout or when responseStatus is still unknown (prevents initial flash)
  const shouldShowLoadingModal = shouldShowLoading && !hasTimedOut.current && loadingType === 'generating' && responseStatus !== null;

  // Derived state for skeleton visibility (considers modal state)
  // Show skeletons when data is loading BUT modal is NOT visible
  // When modal is visible, show actual content behind it instead of skeletons
  const shouldShowSkeletons = shouldShowLoading && !showLoadingModal;


  const scrollY = useSharedValue(0);
  const productFitPosition = useSharedValue(0);
  const viewStartTime = useRef(Date.now());

  // Share modal state
  const [isSharing, setIsSharing] = useState(false);
  const [hasShared, setHasShared] = useState(false);
  const [isCustomShareModalVisible, setIsCustomShareModalVisible] = useState(false);
  const [currentShareUrl, setCurrentShareUrl] = useState('');
  const [currentShareTitle, setCurrentShareTitle] = useState('');
  const [currentShareMessagePrefix, setCurrentShareMessagePrefix] = useState('');
  const [containerHeight, setContainerHeight] = useState(0);
  const [headerHeight, _setHeaderHeight] = useState(0);
  const slideAnimation = useRef(new RNAnimated.Value(0)).current;
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);

  // Report generation state
  const { user, session, logout, region, onboardingIntent } = useAuth();
  const { hasProAccess, triggerUpgradeModal } = useTier();
  const [existingReport, setExistingReport] = useState(null);
  const [_isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [showReportNotification, setShowReportNotification] = useState(false);
  const [lastGenerationTime, setLastGenerationTime] = useState(0);
  const RATE_LIMIT_MS = 5000; // 5 seconds between report generation requests

  // Real-time report updates
  const { report: realtimeReport } = useReportRealtime(existingReport?.id, session);

  // --- MY REPORTS MENU VISIBILITY ---
  // Recommendations power the AddCarModal quick-pick (notSelectedRecommendations / selectedModelIds).
  // Only fetch in live mode (not shared mode).
  const { recommendations } = useRealtimeRecommendations(mode === 'live' ? session : null, 'batch');

  // Not-selected models for quick-pick cards in AddCarModal
  const notSelectedRecommendations = useMemo(
    () => recommendations.filter((rec) => !rec.selected_for_feed),
    [recommendations]
  );
  const selectedModelIds = useMemo(
    () => recommendations.filter((rec) => rec.selected_for_feed).map((rec) => rec.modelId),
    [recommendations]
  );

  const handleQuickSelect = useCallback(async (modelId) => {
    const updatedIds = [...selectedModelIds, modelId];
    try {
      await apiService.persistCarSelection(updatedIds);
    } catch (err) {
      // monetization-step-6 Phase D migrated persist-car-selection to
      // enforceCap, which now returns code='cap_tracked_models' (legacy
      // 'free_tier_model_cap' kept as alias for transition safety).
      const code = err?.code;
      if (
        err?.status === 403 &&
        (code === 'cap_tracked_models' || code === 'free_tier_model_cap')
      ) {
        triggerUpgradeModal({
          source: err?.body?.upgrade_modal_source || 'cap_tracked_models',
          onDismiss: () => {
            Alert.alert('Upgrade required', 'Upgrade to Pro to add more models.');
          },
        });
        return;
      }
      throw err;
    }
    await apiService.triggerListingFeedForModels([modelId]);
    posthog?.capture('quick_pick_added', { model_id: modelId, source: 'car_detail' });
    setTimeout(() => window.location.reload(), 500);
  }, [selectedModelIds, posthog, triggerUpgradeModal]);

  // Handle loading completion - simplified with no retry logic
  const handleLoadingComplete = useCallback(() => {
    // ProductDetailPage requires FULL data ('confirmed'), not just essentials
    const isDataConfirmed = fitScoreData?.data_fidelity === 'confirmed';

    // Mark as timed out to prevent modal from re-appearing
    hasTimedOut.current = true;
    setShowLoadingModal(false);

    // Show notification if timeout reached with unconfirmed data
    if (!isDataConfirmed) {
      setShowTimeoutNotification(true);
      logger.warn('[ProductDetailPage] Loading timeout with unconfirmed data');
      posthog.capture('loading_screen_timeout_reached', {
        screen: 'product_detail',
        model_id: params.id,
        had_partial_data: !!fitScoreData,
      });
    } else {
      posthog.capture('loading_screen_completed', {
        screen: 'product_detail',
        model_id: params.id,
      });
    }
  }, [fitScoreData, params.id, posthog]);

  // Animate share modal slide
  useEffect(() => {
    if (containerHeight > 0) {
      RNAnimated.timing(slideAnimation, {
        toValue: isCustomShareModalVisible ? 1 : 0,
        duration: 400,
        easing: Easing.bezier(0.25, 0.1, 0.25, 1),
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    }
  }, [isCustomShareModalVisible, containerHeight, slideAnimation]);

  // Fetch existing report on mount (only in live mode)
  useEffect(() => {
    const fetchExistingReport = async () => {
      if (mode === 'shared' || !session?.user || !model_id) return;

      try {
        const { data, error } = await supabase
          .from('vehicle_reports')
          .select('id, status, pdf_url')
          .eq('user_id', session.user.id)
          .eq('model_id', model_id)
          .maybeSingle();

        if (error) {
          logger.error('[ProductDetailPage] Error fetching existing report:', error);
          return;
        }

        if (data) {
          setExistingReport(data);
        }
      } catch (error) {
        logger.error('[ProductDetailPage] Exception fetching existing report:', error);
      }
    };

    fetchExistingReport();
  }, [model_id, session, mode]);

  // Update existingReport when realtime data changes
  useEffect(() => {
    if (realtimeReport) {
      setExistingReport({
        id: realtimeReport.id,
        status: realtimeReport.status,
        pdf_url: realtimeReport.pdf_url,
      });
    }
  }, [realtimeReport]);

  // Track page view
  useEffect(() => {
    // Start tracking time on this page (always run, even while loading)
    startPageTracking('product_detail');
    trackProductView(model_id);
    trackSessionEvent();

    const eventName = mode === 'shared' ? 'snapshot_viewed' : 'product_detail_viewed';

    // Normalize vehicle data and extract properties
    const normalizedVehicle = normalizeVehicleData(vehicleData);
    const vehicleProps = getVehicleProperties(normalizedVehicle);
    const recommendationProps = getRecommendationContext(fitScoreData);
    const sessionProps = getSessionContext();
    const pageProps = getPageContext();

    // Only fire PostHog event once per model_id when data is ready (prevent duplicates)
    // In shared mode, data is always ready. In live mode, wait for data to load.
    const shouldFireEvent =
      mode === 'shared' ||
      (!isLoadingVehicle && vehicleData && posthogViewTrackedRef.current !== model_id);

    if (shouldFireEvent) {
      posthog.capture(eventName, {
        model_id: model_id,
        source: params.source || 'unknown',
        timestamp: new Date().toISOString(),
        // Vehicle context
        ...vehicleProps,
        // Recommendation context
        ...recommendationProps,
        // Session context
        ...sessionProps,
        // Page context
        ...pageProps,
        // Mode
        is_shared_view: mode === 'shared',
      });

      // Mark this model_id as tracked (only for live mode, shared can fire multiple times)
      if (mode !== 'shared') {
        posthogViewTrackedRef.current = model_id;
      }
    }

    // Track product view in GTM (only for live mode, not shared snapshots)
    // Only fire once per model_id to prevent duplicates
    if (mode !== 'shared' && productViewTrackedRef.current !== model_id) {
      // Build display name from make and model
      const displayName = vehicleProps
        ? `${vehicleProps.vehicle_make || ''} ${vehicleProps.vehicle_model || ''}`.trim() ||
          'Unknown'
        : 'Unknown';

      trackEngagement('product_viewed', {
        item_id: model_id,
        item_name: displayName,
        item_variant: vehicleProps?.variant_name,
        source: params.source || 'unknown',
      });

      // Mark this model_id as tracked
      productViewTrackedRef.current = model_id;
    }

    return () => {
      // Track time spent when leaving
      const timeSpent = Math.floor((Date.now() - viewStartTime.current) / 1000);
      trackSessionEvent();

      posthog.capture('product_detail_time_spent', {
        model_id: model_id,
        time_spent_seconds: timeSpent,
        ...vehicleProps,
        ...getSessionContext(),
      });
    };
  }, [
    model_id,
    mode,
    params.source,
    vehicleData,
    fitScoreData,
    isLoadingVehicle,
    posthog.capture,
  ]);

  // Modal visibility control
  useEffect(() => {
    if (shouldShowLoadingModal && !showLoadingModal) {
      logger.log('[ProductDetailPage] Showing loading modal', {
        loadingType,
        responseStatus,
        shouldShowLoadingModal,
      });
      setShowLoadingModal(true);
      posthog.capture('loading_screen_shown', {
        screen: 'product_detail',
        model_id: params.id,
        loading_type: loadingType,
      });
    } else if (!shouldShowLoadingModal && showLoadingModal) {
      logger.log('[ProductDetailPage] Hiding loading modal');
      handleLoadingComplete();
    }
  }, [
    shouldShowLoadingModal,
    showLoadingModal,
    handleLoadingComplete,
    loadingType,
    params.id,
    posthog,
    responseStatus,
  ]);

  // Error handling
  useEffect(() => {
    if (error && showLoadingModal) {
      logger.error('[ProductDetailPage] Loading error:', error);
      setShowLoadingModal(false);
      posthog.capture('loading_screen_error', {
        screen: 'product_detail',
        model_id: params.id,
        error: error.message || 'Unknown error',
      });
    }
  }, [error, showLoadingModal, params.id, posthog]);

  const scrollHandler = useAnimatedScrollHandler((event) => {
    scrollY.set(event.contentOffset.y);
  });

  const _handleLogoPress = () => {
    posthog.capture('product_detail_logo_clicked');
    Linking.openURL('https://doocars.com');
  };

  const handleCloseCustomShareModal = () => {
    setIsCustomShareModalVisible(false);
    setHasShared(true);
  };

  const handleNativeShare = async () => {
    if (currentShareUrl) {
      try {
        await Share.share({
          message: `${currentShareMessagePrefix}${currentShareUrl}`,
          title: currentShareTitle,
        });
      } catch (_error) {
        // User dismissed share sheet
      }
    }
  };

  const handleReportPress = async () => {
    if (!model_id) {
      logger.warn('[ProductDetailPage] Cannot generate report: model_id is missing');
      return;
    }

    const reportStatus = existingReport?.status;

    // If report is complete, navigate to report view
    if (reportStatus === 'complete') {
      posthog.capture('report_view_clicked', {
        model_id: model_id,
        report_id: existingReport.id,
      });
      router.push(`/reports/${existingReport.id}`);
      return;
    }

    // Rate limiting check - prevent spam clicking
    const now = Date.now();
    const timeSinceLastGeneration = now - lastGenerationTime;

    if (timeSinceLastGeneration < RATE_LIMIT_MS) {
      const remainingSeconds = Math.ceil((RATE_LIMIT_MS - timeSinceLastGeneration) / 1000);
      Alert.alert(
        'Please Wait',
        `You can only generate one report every ${RATE_LIMIT_MS / 1000} seconds. Please wait ${remainingSeconds} more second${remainingSeconds > 1 ? 's' : ''}.`
      );
      logger.log('[ProductDetailPage] Report generation rate limited:', {
        timeSinceLastGeneration,
        remainingSeconds,
      });
      return;
    }

    // Update last generation time
    setLastGenerationTime(now);

    // Free PDF report 1-lifetime cap
    if (!hasProAccess && session?.user?.id) {
      try {
        const { count } = await supabase
          .from('vehicle_reports')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', session.user.id);
        if ((count || 0) >= 1) {
          posthog.capture('pdf_report_cap_hit', { lifetime_count: count || 0 });
          triggerUpgradeModal({ source: 'cap_pdf_report' });
          return;
        }
      } catch (capError) {
        logger.error('[ProductDetailPage] PDF cap check failed:', capError);
      }
    }

    // Otherwise, generate new report
    setIsGeneratingReport(true);
    try {
      const { report_id } = await apiService.invokeEdgeFunction('preinitiate-report-job', {
        body: { model_id: model_id },
      });

      // Show inline notification instead of Alert
      setShowReportNotification(true);

      // Update local state
      setExistingReport({ id: report_id, status: 'pending' });

      // Track event
      // Extract vehicle and session context
      const normalizedVehicle = normalizeVehicleData(vehicleData);
      const vehicleProps = getVehicleProperties(normalizedVehicle);
      const sessionProps = getSessionContext();

      trackSessionEvent();
      posthog.capture('report_generation_initiated', {
        model_id: model_id,
        report_id: report_id,
        source: 'product_detail_page',
        ...vehicleProps,
        ...sessionProps,
        time_on_page_before_generation: Math.floor((Date.now() - viewStartTime.current) / 1000),
      });

      // Track report generation in GTM with enhanced conversion data (requires user email)
      // Build display name from make and model
      const displayName = vehicleProps
        ? `${vehicleProps.vehicle_make || ''} ${vehicleProps.vehicle_model || ''}`.trim() ||
          'Unknown'
        : 'Unknown';

      // Track as conversion only if user email is available (matches onboarding_started pattern)
      if (user?.email) {
        trackConversion(
          'report_generation_initiated',
          {
            item_id: model_id,
            report_id: report_id,
            item_name: displayName,
            source: 'product_detail_page',
          },
          { email: user.email }
        );
      } else {
        // Log warning - this screen requires auth, missing email is unexpected
        logger.warn('[ProductDetailPage] User email not available for conversion tracking', {
          modelId: model_id,
          reportId: report_id,
          userId: user?.id,
        });
      }

      logger.log('[ProductDetailPage] Report generation initiated:', report_id);
    } catch (error) {
      logger.error('[ProductDetailPage] Error generating report:', error);

      // monetization-step-6 Phase H sonnet H4: surface pdf_report cap hits
      // (Free lifetime, Trial trial_lifetime, Pro rolling_1d/30d burst).
      // Free tier carries upgrade_modal_source -> upgrade modal; Trial/Pro
      // burst rows have null source -> Alert with the cap message.
      const code = error?.code || '';
      const body = error?.body;
      if (code && code.startsWith('cap_pdf_report')) {
        if (body?.upgrade_modal_source) {
          triggerUpgradeModal({ source: body.upgrade_modal_source });
        } else {
          Alert.alert('Report limit reached', body?.message || error.message);
        }
      } else {
        Alert.alert('Error', `Could not generate report: ${error.message}`);
      }

      // On error, reset timer to allow immediate retry
      setLastGenerationTime(0);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleShare = async () => {
    // Don't allow sharing in shared mode
    if (mode === 'shared') {
      return;
    }

    const isNativeMobile = Platform.OS === 'ios' || Platform.OS === 'android';
    const isMobileWeb = Platform.OS === 'web' && screenWidth < BREAKPOINT_WIDTH;
    const hasWebShareAPI =
      Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.share;

    if (isNativeMobile) {
      // On native mobile (iOS/Android), directly trigger the native share
      setIsSharing(true);
      try {
        const { share_url, share_token } = await apiService.invokeEdgeFunction(
          'create-shared-snapshot',
          {
            body: { model_id: model_id },
          }
        );

        if (share_url) {
          trackSessionEvent();
          posthog.capture('snapshot_created', {
            model_id: model_id,
            share_token: share_token,
            ...getVehicleProperties(normalizeVehicleData(vehicleData)),
            ...getSessionContext(),
            share_method: 'native',
          });

          await Share.share({
            message: `Check out this car recommendation from doocars.com\n${share_url}`,
            title: 'Check out this car recommendation from doocars.com!',
          });
          setHasShared(true);
        } else {
          throw new Error('Could not get a shareable link.');
        }
      } catch (error) {
        const errorMessage = `Could not create share link: ${error.message}. Please try again.`;
        Alert.alert('Error', errorMessage);
      } finally {
        setIsSharing(false);
      }
    } else if (isMobileWeb && hasWebShareAPI) {
      // On mobile web with Web Share API support, use native OS share menu
      setIsSharing(true);
      try {
        const { share_url, share_token } = await apiService.invokeEdgeFunction(
          'create-shared-snapshot',
          {
            body: { model_id: model_id },
          }
        );

        if (share_url) {
          trackSessionEvent();
          posthog.capture('snapshot_created', {
            model_id: model_id,
            share_token: share_token,
            ...getVehicleProperties(normalizeVehicleData(vehicleData)),
            ...getSessionContext(),
            share_method: 'web_share_api',
          });

          await navigator.share({
            title: 'Check out this car recommendation from doocars.com!',
            text: 'Check out this car recommendation!',
            url: share_url,
          });
          setHasShared(true);
        } else {
          throw new Error('Could not get a shareable link.');
        }
      } catch (error) {
        // User cancelled or error occurred
        if (error.name !== 'AbortError') {
          const errorMessage = `Could not create share link: ${error.message}. Please try again.`;
          Alert.alert('Error', errorMessage);
        }
      } finally {
        setIsSharing(false);
      }
    } else {
      // On desktop or mobile web without Web Share API, show the custom modal
      // If modal is already visible, don't create a new share URL
      if (isCustomShareModalVisible) {
        return;
      }

      setIsSharing(true);
      try {
        const { share_url, share_token } = await apiService.invokeEdgeFunction(
          'create-shared-snapshot',
          {
            body: { model_id: model_id },
          }
        );

        if (share_url) {
          trackSessionEvent();
          posthog.capture('snapshot_created', {
            model_id: model_id,
            share_token: share_token,
            ...getVehicleProperties(normalizeVehicleData(vehicleData)),
            ...getSessionContext(),
            share_method: 'custom_modal',
          });

          const title = 'Check out this car recommendation!';
          const messagePrefix = 'Check out this car recommendation!\n';

          setCurrentShareUrl(share_url);
          setCurrentShareTitle(title);
          setCurrentShareMessagePrefix(messagePrefix);
          setIsCustomShareModalVisible(true);
          setIsSharing(false);
        } else {
          throw new Error('Could not get a shareable link.');
        }
      } catch (error) {
        const errorMessage = `Could not create share link: ${error.message}. Please try again.`;
        Alert.alert('Error', errorMessage);
        setIsSharing(false);
      }
    }
  };

  const handleRetry = () => {
    posthog.capture('product_detail_error_retry', {
      model_id: params.id,
      error_message: error,
    });
    refetchData();
  };

  const handleBackToDashboard = () => {
    posthog.capture('product_detail_error_back_to_dashboard', {
      model_id: params.id,
      error_message: error,
    });
    router.push('/home');
  };

  const handleFindMyCar = () => {
    posthog.capture('shared_snapshot_find_my_car_clicked', {
      model_id: model_id,
    });
    router.push('/onboarding');
  };

  // Unified navigation links (handles both shared and live mode)
  const navLinks = useNavLinks({ logout, session, mode });

  // Early return for error state - show error UI with recovery options
  if (error && !vehicleData) {
    return (
      <View style={styles.contentWrapper}>
        <ErrorView error={error} onRetry={handleRetry} onBack={handleBackToDashboard} />
      </View>
    );
  }

  // Transform data for component props using transformer functions
  // Extract model images from vehicleData if available
  // In shared mode, images are in imageList; in live mode, they're in images
  const modelImages = vehicleData?.imageList || vehicleData?.images || [];

  // Transform data for ProductData component
  const productDataProps = transformVehicleDataForProductData(
    vehicleData,
    modelImages,
    fitScoreData
  );

  // Transform data for ProductFit component (uses personalizedContent from user_model_page_content)
  const productFitProps = transformFitScoreForProductFit(personalizedContent);

  // Transform data for ProductInfo component
  const productInfoProps = transformPersonalizedContentForProductInfo(personalizedContent);

  // Transform data for ProductSide component (Task 12a: pass region for regional field extraction)
  const productSideProps = transformVehicleDataForProductSide(
    vehicleData,
    personalizedContent,
    fitScoreData,
    onboardingData,
    region
  );

  // Derive vehicle model display name for ListingsFeed header
  const vehicleModelDisplayName = useMemo(() => {
    if (!vehicleData) return '';
    const make = vehicleData.make || vehicleData.manufacturer || '';
    const model = vehicleData.model || vehicleData.model_name || '';
    return `${make} ${model}`.trim();
  }, [vehicleData]);

  // Skeleton Components for loading states
  const ProductDataSkeleton = () => (
    <View style={styles.skeletonContainer}>
      <View style={styles.productDataSkeletonHeader}>
        <View>
          <SkeletonPlaceholder width={250} height={36} style={{ marginBottom: 12 }} />
          <SkeletonPlaceholder width={180} height={20} />
        </View>
        <SkeletonPlaceholder width={120} height={40} style={{ borderRadius: 20 }} />
      </View>
      <View style={styles.productDataSkeletonImages}>
        <SkeletonPlaceholder width="100%" height={400} style={{ borderRadius: 12 }} />
      </View>
    </View>
  );

  const ProductFitSkeleton = () => (
    <View
      style={[
        styles.productFitSkeletonContainer,
        isMobile && styles.productFitSkeletonContainerMobile,
      ]}
    >
      <View style={styles.productFitSkeletonContent}>
        <SkeletonPlaceholder width={300} height={32} style={{ marginBottom: 24 }} />
        <View style={styles.productFitSkeletonMetrics}>
          <SkeletonPlaceholder
            width={150}
            height={120}
            style={{ borderRadius: 12, marginRight: 16 }}
          />
          <SkeletonPlaceholder
            width={150}
            height={120}
            style={{ borderRadius: 12, marginRight: 16 }}
          />
          <SkeletonPlaceholder width={150} height={120} style={{ borderRadius: 12 }} />
        </View>
        <SkeletonPlaceholder width="100%" height={80} style={{ marginTop: 24, borderRadius: 8 }} />
      </View>
    </View>
  );

  const ProductInfoSkeleton = () => (
    <View style={styles.productInfoSkeletonContainer}>
      <View style={styles.productInfoSkeletonSection}>
        <SkeletonPlaceholder width={200} height={28} style={{ marginBottom: 16 }} />
        <SkeletonPlaceholder width="100%" height={20} style={{ marginBottom: 12 }} />
        <SkeletonPlaceholder width="100%" height={20} style={{ marginBottom: 12 }} />
        <SkeletonPlaceholder width="90%" height={20} style={{ marginBottom: 24 }} />
      </View>
      <View style={styles.productInfoSkeletonSection}>
        <SkeletonPlaceholder width={180} height={28} style={{ marginBottom: 16 }} />
        <SkeletonPlaceholder width="100%" height={20} style={{ marginBottom: 12 }} />
        <SkeletonPlaceholder width="95%" height={20} style={{ marginBottom: 12 }} />
        <SkeletonPlaceholder width="100%" height={20} style={{ marginBottom: 24 }} />
      </View>
      <View style={styles.productInfoSkeletonSection}>
        <SkeletonPlaceholder width={220} height={28} style={{ marginBottom: 16 }} />
        <SkeletonPlaceholder width="100%" height={20} style={{ marginBottom: 12 }} />
        <SkeletonPlaceholder width="100%" height={20} style={{ marginBottom: 12 }} />
        <SkeletonPlaceholder width="85%" height={20} />
      </View>
    </View>
  );

  const ProductSideSkeleton = () => (
    <View style={styles.productSideSkeletonContainer}>
      <SkeletonPlaceholder
        width="100%"
        height={60}
        style={{ marginBottom: 24, borderRadius: 12 }}
      />
      <View style={styles.productSideSkeletonInputs}>
        <SkeletonPlaceholder
          width="100%"
          height={48}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
        <SkeletonPlaceholder
          width="100%"
          height={48}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
        <SkeletonPlaceholder
          width="100%"
          height={48}
          style={{ marginBottom: 24, borderRadius: 8 }}
        />
      </View>
      <SkeletonPlaceholder width="100%" height={200} style={{ borderRadius: 12 }} />
    </View>
  );

  return (
    <View
      style={[styles.contentWrapper, isMobile && styles.mobileContentWrapper]}
      onLayout={(event) => {
        const { height } = event.nativeEvent.layout;
        setContainerHeight(height);
      }}
    >
      {/* Mobile header outside scrollview for edge-to-edge layout */}
      {isMobile && (
        <MobileHeader
          logo={Logo}
          navLinks={mode !== 'shared' ? navLinks : []}
          isDark
          onLogout={mode !== 'shared' ? logout : undefined}
          onSearchPress={mode !== 'shared' ? () => setIsSearchModalVisible(true) : undefined}
          onSharePress={mode !== 'shared' ? handleShare : undefined}
          onLogoPress={mode !== 'shared' ? () => router.push('/home') : undefined}
        />
      )}

      <Animated.ScrollView
        contentContainerStyle={styles.scrollContent}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
      >
        {/* Desktop header outside scrollview for consistent positioning */}
        {!isMobile && (
          <DesktopHeader
            logo={Logo}
            navLinks={mode !== 'shared' ? navLinks : []}
            variant="dark"
            onSearchPress={mode !== 'shared' ? () => setIsSearchModalVisible(true) : undefined}
            onLogoPress={mode !== 'shared' ? () => router.push('/home') : undefined}
          />
        )}

        <View style={[styles.centeredContent, isMobile && styles.centeredContentMobile]}>
          {/* Snapshot banner - only show in shared mode */}
          {mode === 'shared' && (
            <View style={[styles.snapshotBanner, isMobile && styles.snapshotBannerMobile]}>
              <Text style={styles.snapshotBannerText}>
                This is a shared snapshot and may not reflect the latest information.
              </Text>
            </View>
          )}

          {/* Back button - only show in live mode */}
          {mode !== 'shared' && (
            <TouchableOpacity
              style={[styles.backButton, isMobile && styles.backButtonMobile]}
              onPress={() => {
                posthog.capture('back_to_recommendations_clicked', {
                  model_id: params.id,
                });
                router.canGoBack() ? router.back() : router.push('/home');
              }}
            >
              <Icon name="arrow-left" size={16.7} color={REDESIGN_COLORS.textSecondary} />
              <Text style={styles.backButtonText}>Back to recommendations</Text>
            </TouchableOpacity>
          )}

          <View
            style={[styles.productDataContainer, isMobile && styles.productDataContainerMobile]}
          >
            {shouldShowSkeletons ? (
              <ProductDataSkeleton />
            ) : (
              <ProductData
                {...productDataProps}
                hideStyleMatch={onboardingIntent === 'deals'}
                mode={mode}
                onFindMyCar={handleFindMyCar}
                modelId={model_id}
                existingReport={existingReport}
                session={session}
                onReportPress={handleReportPress}
                showSuccessNotification={showReportNotification}
                onNotificationDismiss={() => setShowReportNotification(false)}
              />
            )}
          </View>
        </View>
        {/* Listings Feed — shown in live mode above the personalised section */}
        {mode === 'live' && model_id && (
          <View style={[styles.centeredContent, isMobile && styles.centeredContentMobile]}>
            <MemoizedListingsFeed
              modelId={model_id}
              vehicleModel={vehicleModelDisplayName}
              fitScore={fitScoreData?.score_value}
              vehicleModelImageUrl={modelImages?.[0]?.url}
            />
          </View>
        )}

        <View
          collapsable={false}
          style={styles.productFitWrapper}
          onLayout={(event) => {
            productFitPosition.set(event.nativeEvent.layout.y);
          }}
        >
          {shouldShowSkeletons ? (
            <ProductFitSkeleton />
          ) : (
            <ProductFit
              {...productFitProps}
              scrollY={scrollY}
              containerPosition={productFitPosition}
            />
          )}
        </View>

        <View style={styles.backgroundSection}>
          <View style={styles.backgroundRectangle} />
        </View>
        <View
          style={[
            styles.centeredContent,
            isMobile && styles.centeredContentMobile,
            { position: 'relative', zIndex: 2, backgroundColor: 'transparent', paddingBottom: 40 },
          ]}
        >
          <View
            style={[styles.mainContentContainer, isMobile && styles.mainContentContainerMobile]}
          >
            <View style={[styles.leftColumn, isMobile && styles.leftColumnMobile]}>
              {shouldShowSkeletons ? <ProductInfoSkeleton /> : <ProductInfo {...productInfoProps} />}
            </View>
            <View style={[styles.rightColumn, isMobile && styles.rightColumnMobile]}>
              {shouldShowSkeletons ? (
                <ProductSideSkeleton />
              ) : (
                <ProductSide
                  calculatorInputData={productSideProps}
                  region={region}
                />
              )}
            </View>
          </View>
        </View>

        {/* Footer */}
        <Footer
          content={landingPageContent.footer}
          onNavPress={(target) => router.push(target)}
          variant="dark"
          hideLogo={mode !== 'shared'}
          style={{
            marginTop: 40,
            paddingTop: 16,
            paddingBottom: (screenWidth >= BREAKPOINT_WIDTH && mode === 'live' ? 20 : 16) + bottomInset
          }}
        />
      </Animated.ScrollView>

      {/* Share button - only show on desktop in live mode */}
      {screenWidth >= BREAKPOINT_WIDTH && mode === 'live' && (
        <>
          {/* Share button (visible when modal is closed) */}
          {!isCustomShareModalVisible && (
            <View style={styles.shareButtonContainer}>
              <TouchableOpacity
                onPress={handleShare}
                disabled={isSharing || hasShared}
                style={styles.shareButton}
              >
                <Text style={styles.shareButtonText}>
                  {hasShared ? 'Shared' : isSharing ? 'Creating Link...' : 'Share with friends'}
                </Text>
                <Icon name="chevron-up" size={20} color="#FFFFFF" />
              </TouchableOpacity>
            </View>
          )}

          {/* The close button (floats on top) - only show when modal is open */}
          {isCustomShareModalVisible && (
            <RNAnimated.View style={[styles.closeButtonContainer, { opacity: slideAnimation }]}>
              <TouchableOpacity onPress={handleCloseCustomShareModal} style={styles.shareButton}>
                <Text style={styles.shareButtonText}>Share with friends</Text>
                <Icon name="chevron-down" size={20} color="#FFFFFF" />
              </TouchableOpacity>
            </RNAnimated.View>
          )}
        </>
      )}

      {/* Share modal - only render when visible to avoid blocking touches */}
      {mode === 'live' && containerHeight > 0 && isCustomShareModalVisible && (
        <RNAnimated.View
          style={{
            position: 'absolute',
            top: headerHeight,
            left: 0,
            right: 0,
            bottom: 0,
            transform: [
              {
                translateY: slideAnimation.interpolate({
                  inputRange: [0, 1],
                  outputRange: [containerHeight, 0],
                }),
              },
            ],
            zIndex: 10,
          }}
        >
          <CustomShareModal shareUrl={currentShareUrl} onNativeShare={handleNativeShare} />

          {/* Close button for mobile (top right X) */}
          {screenWidth < BREAKPOINT_WIDTH && (
            <RNAnimated.View style={[styles.mobileShareCloseButton, { opacity: slideAnimation }]}>
              <TouchableOpacity
                onPress={handleCloseCustomShareModal}
                style={styles.closeIconButton}
              >
                <Icon name="close" size={24} color="#FFFFFF" />
              </TouchableOpacity>
            </RNAnimated.View>
          )}
        </RNAnimated.View>
      )}

      {/* Loading Modal */}
      {showLoadingModal && (
        <>
          {logger.log('[ProductDetailPage] Rendering modal with config:', {
            loadingType,
            config:
              loadingType === 'generating'
                ? 'PRODUCT_PAGE_GENERATING_CONFIG'
                : 'PRODUCT_PAGE_LOADING_CONFIG',
            showLoadingModal,
          })}
        </>
      )}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showLoadingModal}
        onRequestClose={() => {}}
        accessibilityViewIsModal={true}
        statusBarTranslucent={true}
      >
        <View style={styles.modalOverlay}>
          <LoadingScreen
            {...(loadingType === 'generating'
              ? PRODUCT_PAGE_GENERATING_CONFIG
              : PRODUCT_PAGE_LOADING_CONFIG)}
            useBlurBackground={true}
            blurIntensity={15}
            blurTint="light"
            onComplete={handleLoadingComplete}
          />
        </View>
      </Modal>

      {/* Timeout Notification */}
      <InlineNotification
        visible={showTimeoutNotification}
        type="error"
        message="We're experiencing heavy load. Please refresh or come back later."
        onDismiss={() => setShowTimeoutNotification(false)}
        duration={8000}
      />

      {/* Add Car Modal */}
      <AddCarModal
        visible={isSearchModalVisible}
        onClose={() => setIsSearchModalVisible(false)}
        recommendedModels={notSelectedRecommendations}
        onQuickSelect={handleQuickSelect}
        addedVia="product_detail"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  contentWrapper: {
    flex: 1,
    backgroundColor: 'transparent',
    overflow: 'hidden',
  },
  mobileContentWrapper: {
    backgroundColor: 'transparent',
  },
  desktopHeaderWrapper: {
    paddingTop: 24,
    width: '100%',
  },
  centeredContent: {
    maxWidth: 1440,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: 32,
  },
  centeredContentMobile: {
    paddingHorizontal: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  navLinks: {
    flexDirection: 'row',
    gap: 40,
  },
  navLink: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: REDESIGN_COLORS.textPrimary,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 100,
  },
  backButtonMobile: {
    marginTop: 24,
  },
  backButtonText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: REDESIGN_COLORS.textSecondary,
  },
  productDataContainer: {
    marginTop: 40,
  },
  productDataContainerMobile: {
    marginTop: 24,
  },
  mainContentContainer: {
    flex: 1,
    flexDirection: 'row',
    marginTop: 86,
    paddingHorizontal: 48,
    gap: 40,
  },
  mainContentContainerMobile: {
    flexDirection: 'column',
    marginTop: 32,
    paddingHorizontal: 16,
    gap: 24,
  },
  leftColumn: {
    width: 812,
  },
  leftColumnMobile: {
    width: '100%',
  },
  rightColumn: {
    width: 412,
  },
  rightColumnMobile: {
    width: '100%',
  },
  shareButtonContainer: {
    position: 'absolute',
    bottom: 24,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 1,
  },
  shareButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 16,
    gap: 10,
    backgroundColor: '#000000',
  },
  shareButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    color: '#FFFFFF',
    letterSpacing: -0.14,
  },
  scrollContent: {
    flexGrow: 0,
  },
  backgroundSection: {
    position: 'absolute',
    top: 1128,
    left: 0,
    right: 0,
    bottom: 0,
    minHeight: 200,
    zIndex: -1,
  },
  backgroundRectangle: {
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
  },
  productFitWrapper: {
    width: '100%',
    alignItems: 'center',
  },
  // Skeleton styles
  skeletonContainer: {
    width: '100%',
  },
  productDataSkeletonHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 32,
  },
  productDataSkeletonImages: {
    width: '100%',
  },
  productFitSkeletonContainer: {
    width: '100%',
    maxWidth: 1440,
    paddingHorizontal: 32,
    paddingVertical: 48,
  },
  productFitSkeletonContainerMobile: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  productFitSkeletonContent: {
    alignItems: 'center',
  },
  productFitSkeletonMetrics: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  productInfoSkeletonContainer: {
    width: '100%',
  },
  productInfoSkeletonSection: {
    marginBottom: 32,
  },
  productSideSkeletonContainer: {
    width: '100%',
    padding: 24,
    backgroundColor: REDESIGN_COLORS.cardBg,
    borderRadius: 16,
  },
  productSideSkeletonInputs: {
    marginBottom: 24,
  },
  // Error UI styles
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
    backgroundColor: 'transparent',
  },
  errorContent: {
    maxWidth: 500,
    alignItems: 'center',
    textAlign: 'center',
  },
  errorIcon: {
    marginBottom: 24,
  },
  errorTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 28,
    color: REDESIGN_COLORS.textPrimary,
    marginBottom: 16,
    textAlign: 'center',
  },
  errorMessage: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 16,
    color: REDESIGN_COLORS.textSecondary,
    marginBottom: 32,
    textAlign: 'center',
    lineHeight: 24,
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000000',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 12,
    marginBottom: 16,
    minWidth: 200,
    gap: 8,
  },
  retryButtonIcon: {
    marginRight: 4,
  },
  retryButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 16,
    color: '#FFFFFF',
    letterSpacing: -0.16,
  },
  errorBackButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  errorBackButtonText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: REDESIGN_COLORS.textSecondary,
    textDecorationLine: 'underline',
  },
  // Snapshot banner styles
  snapshotBanner: {
    padding: 10,
    backgroundColor: 'rgba(255, 183, 77, 0.15)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 183, 77, 0.3)',
    marginTop: 40, // Desktop: clear the absolutely positioned header (default is desktop)
    borderRadius: 8,
    position: 'relative',
    zIndex: 3, // Ensure banner is visible above other content
  },
  snapshotBannerMobile: {
    marginTop: 16, // Mobile: less margin needed
  },
  snapshotBannerText: {
    textAlign: 'center',
    color: 'rgba(255, 183, 77, 0.9)',
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
  },
  closeButtonContainer: {
    position: 'absolute',
    bottom: 24,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 11,
  },
  mobileShareCloseButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 12,
  },
  closeIconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
});

export default ProductDetailPage;
